import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { analytics } from "MVVM/Analytics";
import { UserSearchViewModel } from "./ViewModel";
import { ISearchItem, NavSearchBar } from "../SearchBar/NavSearchBar";
import { AllstarUser } from "MVVM/State";
import { addRecentSearch } from "MVVM/Utilities/RecentSearches";

interface IUser {
  _id: string;
  id: string;
  avatar: string;
  avatarUrl: string;
  username: string;
  discord: {
    id: string;
  };
}

interface IProPlayer {
  id: string;
  name: string;
  avatar?: string;
  game?: string;
  gameIdentifier?: string;
}

const STEAM_ID_REGEX = /^\d{17}$/;

const UserSearch = ({
  compact,
}: {
  // The new site nav wants a tighter control: no preview avatars, short hint.
  compact?: boolean;
} = {}) => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [proPlayers, setProPlayers] = useState<IProPlayer[]>([]);
  const [steamProfile, setSteamProfile] = useState<ISearchItem | null>(null);
  const history = useHistory();
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    userSearchQuery,
    loading,
    proPlayerSearchQuery,
    proPlayerLoading,
    steamLookupQuery,
    steamLookupLoading,
  } = UserSearchViewModel();
  const allstarUser = useRecoilValue(AllstarUser);
  const [currentQuery, setCurrentQuery] = useState("");

  const transformUserToSearchItem = (user: IUser): ISearchItem => ({
    id: user.id,
    name: user.username,
    image: user.avatarUrl || user.avatar,
  });

  const transformProPlayerToSearchItem = (player: IProPlayer): ISearchItem => ({
    id: player.gameIdentifier || player.id,
    name: player.name,
    image: player.avatar || "",
    isProPlayer: true,
    game: player.game,
  });

  const searchUsers = useCallback(
    (query: string) => {
      setCurrentQuery(query);

      if (!query.trim()) {
        setUsers([]);
        setProPlayers([]);
        setSteamProfile(null);
        return;
      }

      // A raw steamid64 can't match a name search, so look up the profile directly.
      if (STEAM_ID_REGEX.test(query.trim())) {
        setUsers([]);
        setProPlayers([]);
        steamLookupQuery({ variables: { query: query.trim() } })
          .then((res) => {
            const profile = res.data?.profile;
            setSteamProfile(
              profile
                ? {
                    id: query.trim(),
                    name: profile.username,
                    image: profile.avatarUrl || "",
                    isSteamLookup: true,
                  }
                : null,
            );
          })
          .catch(() => setSteamProfile(null));
        return;
      }

      setSteamProfile(null);

      userSearchQuery({ variables: { count: 10, query } })
        .then((res) => setUsers(res.data?.userSearch ?? []))
        .catch(() => setUsers([]));

      proPlayerSearchQuery({ variables: { count: 3, query } })
        .then((res) => setProPlayers(res.data?.proPlayerSearch ?? []))
        .catch(() => setProPlayers([]));
    },
    [userSearchQuery, proPlayerSearchQuery, steamLookupQuery],
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debouncedSearchTimeoutRef.current)
        clearTimeout(debouncedSearchTimeoutRef.current);

      debouncedSearchTimeoutRef.current = setTimeout(() => {
        searchUsers(query);
      }, 300);
    },
    [searchUsers],
  );

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  // Rank users and pros together by how well the name matches, so a strong
  // user match isn't buried under fuzzy pro results.
  const matchScore = (item: ISearchItem): number => {
    const name = item.name?.toLowerCase() ?? "";
    const q = currentQuery.trim().toLowerCase();
    if (!q) return 3;
    if (name === q) return 0;
    if (name.startsWith(q)) return 1;
    if (name.includes(q)) return 2;
    return 3;
  };

  const mergedItems = [
    ...(steamProfile ? [steamProfile] : []),
    ...[
      ...proPlayers.map(transformProPlayerToSearchItem),
      ...users.map(transformUserToSearchItem),
    ].sort((a, b) => matchScore(a) - matchScore(b)),
  ];

  const onSelect = (item: ISearchItem) => {
    addRecentSearch(item);

    if (item.isSteamLookup) {
      analytics.track("User Search - Click", {
        user: item.id,
        reason: "steamid_lookup",
        search: currentQuery,
        following: false,
      });

      history.push(`/u/${item.id}`);
      return;
    }

    if (item.isProPlayer) {
      analytics.track("User Search - Click", {
        user: item.id,
        reason: "pro_player_search",
        search: currentQuery,
        following: false,
      });

      analytics.track("Button - Click", {
        location: "userSearch",
        locationIdentifier: "userSearchProPlayer",
        source: "website",
      });

      history.push(`/u/${item.id}`);
      return;
    }

    const followingIds = allstarUser?.followers?.following;
    const isFollowing = followingIds
      ? followingIds.map((x: any) => x?._id)?.includes(item.id)
      : false;

    analytics.track("User Search - Click", {
      user: item.id,
      reason: "search",
      search: currentQuery,
      following: isFollowing,
    });

    analytics.track("Button - Click", {
      location: "userSearch",
      locationIdentifier: "userSearchProfile",
      source: "website",
    });

    const location =
      !item.name || item.name === "User"
        ? `/profile?user=${item.id}`
        : `/u/${item.name}`;

    history.push(location);
  };

  const onFocus = () => {
    analytics.track("Search - Focused", {
      location: "user_search",
      locationIdentifier: "userSearch",
    });
  };

  return (
    <>
      <NavSearchBar
        items={mergedItems}
        loading={loading || proPlayerLoading || steamLookupLoading}
        onSelect={onSelect}
        onFocus={onFocus}
        debouncedSearch={debouncedSearch}
        showSteamIdHint={!allstarUser?.loggedIn}
        hideSuggestionsPreview={compact}
        placeholder={
          compact
            ? "Search users or SteamID64"
            : "Search Allstar user or SteamID (ex: 76561198000000000)"
        }
      />
    </>
  );
};

export { UserSearch };
